<template>
	<div class="">
		<div class="d-flex flex-column align-items-center h-full-page" :class="width < 992 ? 'justify-content-center' : 'justify-content-center'">
			<h1 class="text-center mt-3">Choose Username</h1>
			<p class="text-center text-muted mt-1">Choose a username for your account. You may change this in your settings later.</p>

			<div ref="usernameForm" class="d-flex flex-column align-items-center">
				<div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken mt-3 mb-1" :class="formErrors.username ? 'border-danger' : 'border-secondary'">
					<label for="username">
						<i class="me-3 dd-icon bi bi-person-lock"></i>
					</label>
					<input v-model="username" @input="usernameInputAction" type="text" id="username" name="username" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Username" >

	<!--				<span class="rounded-circle d-flex align-items-center" v-if="usernameAvailable !== null && checkingUsername === false" >-->
	<!--	                    <small class="text-center text-muted" style="font-size: 0.75rem" v-html="usernameAvailable ? 'Available' : 'Not Available'" ></small>-->
	<!--	                    <i class="align-items-center" :class="usernameAvailable ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'"></i>-->
	<!--	                </span>-->
	<!--				<span v-if="checkingUsername" class="loading" style="width: 5rem; height: 2.5rem; font-size: 10px;"></span>-->

				</div>
				<div class="d-flex justify-content-start align-items-center gap-2 mb-1">
					<div class="fs-6 d-flex align-items-center">
						<i style="width: 1rem; height: 1rem; cursor: default" class="fs-6 p-0 me-1 bi " :class="usernameCheckClass"></i>
						<small style="font-size: 0.75rem;" class="text-muted" v-html="usernameCheckStatusMessage"></small>
					</div>
					<div class="fs-6 d-flex align-items-center">
						<i style="width: 1rem; height: 1rem; cursor: default" class="fs-6 p-0 me-1 bi" :class="{'text-success bi-check-circle' : username && username.length >= 3, 'text-danger bi-x-circle' : !username || username.length < 3, }"></i>
						<small style="font-size: 0.75rem;" class="text-muted">Minimum 3 character</small>
					</div>
				</div>
				<div v-if="formErrors.username" class="validation-error mt-1 mb-1" v-html="formErrors.username"></div>

				<div class="text-center mt-3"><small class="text-muted">Do not choose a username that is associated with yourself such as your email, address, or real name.</small></div>

				<div class="d-flex flex-column py-4 gap-1 align-items-center w-100">
					<button class="btn btn-link" @click="moveButtonAction('two-factor')">Skip for now</button>
					<button class="btn btn-dark rounded-pill py-7" :disabled="!isUsernameValid" :class="width < 992 ? 'w-75' : 'w-50'" @click="saveUsername">Continue</button>
				</div>
			</div>
		</div>

	</div>
</template>

<script setup>
import { ref, computed } from "vue";
import {useDebounceFn} from "@vueuse/core";
import apiClient from "@/utils/axios.js";
import { useAuthStore } from "@/stores/auth-store.js";
import { useHttpErrorManager } from "@/composables/useHttpErrorManager.js";
import { useAppHelpers } from "@/composables/useAppHelpers.js";

const props = defineProps({
	width: [String, Number],
	username: {
		type: String,
		default: null,
		require: false
	}
});

const emits = defineEmits(['navigationButtonClicked']);

const auth = useAuthStore();
const helper = useAppHelpers();

const usernameForm = ref(null);
const moveButtonAction = (name) => {
	emits('navigationButtonClicked', name);
}

const formErrors = ref({});
const username = ref(props.username ? props.username : '');

const usernameAvailable = ref(null);
const checkingUsername = ref(false);

const usernameCheckStatusMessage = computed(() => {
	if(username.value === auth.user.username){
		return 'Your Current Username';
	}
	else if(checkingUsername.value){
		return 'Checking username availability';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === true) {
		return 'Username Available';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === false) {
		return 'Username Not Available';
	}
	else if(username.value.length < 3 || (username.value.length > 3  && usernameAvailable.value == null)){
		return 'Type Valid Username';
	}
	else if(username.value !== auth.user.username){
		return 'Checking username availability';
	}
});

const usernameCheckClass = computed(() => {
	if(username.value === auth.user.username){
		return 'text-success bi-check-circle';
	}
	else if(checkingUsername.value){
		return 'text-dark bi-arrow-clockwise';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === true) {
		return 'text-success bi-check-circle';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === false) {
		return 'text-danger bi-x-circle';
	}
	else if(username.value.length < 3 || (username.value.length > 3  && usernameAvailable.value == null)){
		return 'text-dark bi-question-circle';
	}
	else if(username.value !== auth.user.username){
		return 'text-dark bi-arrow-clockwise';
	}
});

// Debounce the username check function to prevent excessive API calls
const usernameInputAction = () => {
    removeFormErrorKey('username');
    debounceUsernameCheck();
}

const debounceUsernameCheck = useDebounceFn(async () => {
    await checkUsernameAvailability();
}, 500);

// Function to check if the username is available
const checkUsernameAvailability = async () => {
    if (!username.value) {
        usernameAvailable.value = null;
        // formErrors.value.username = 'Please enter a username to continue.';
        return;
    }

	if (username.value.length < 3) {
		usernameAvailable.value = null;
		// formErrors.value.username = 'Username must be at least 3 characters';
		return;
	}

    try {
        checkingUsername.value = true;
        const response = await apiClient.post('/auth/check-username', {
           username: username.value
        });
        checkingUsername.value = false;
        usernameAvailable.value = response.data.available;
        removeFormErrorKey('username');
    } catch (error) {
        checkingUsername.value = false;
        usernameAvailable.value = null;
        formErrors.value.username = error.response.data.message;
    }
};

// Function to validate username
const validateUsername = () => {

	if(username.value === auth.user.username){
		return true;
	}
	if(checkingUsername.value){
		return false;
	}
    if (!username.value) {
        return false;
    }
	if (username.value.length < 3) {
		return false;
	}
    if (usernameAvailable.value !== null && !usernameAvailable.value) {
        return false;
    }
    return true;
};

const isUsernameValid = computed(() => {
	return validateUsername();
});

const saveUsername = async () => {
	if (!validateUsername()) {
		return;
	}
	if(username.value === auth.user.username){
		moveButtonAction('two-factor');
		return;
	}
	try {
		helper.loaderOn(usernameForm.value);
		const response = await apiClient.post('/auth/save-username', {
			username: username.value
		});
		if (response.status === 200) {
			await auth.FETCH_USER_DATA(true);
			moveButtonAction('two-factor');
		}
	} catch (error) {
		helper.loaderOff(usernameForm.value);
		let errorObj = await useHttpErrorManager().handleError(error);
		if (error.response.status === 422) {
			formErrors.value = errorObj;
			removeFormErrorKey('full_message');
			removeFormErrorKey('alt_message');
			removeFormErrorKey('err_message');
		}
	}
};

const removeFormErrors = () => {
	removeFormErrorKey('username');
}

const removeFormErrorKey = (key) => {
	delete formErrors.value[key];
};

</script>

<style scoped lang="scss">
.w-40 {
	width: 40%;
}
.h-full-page{
	height: calc(100vh - 5px);
}
</style>